import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FuelSavingFormValues, Period, TradeSectionData, UseFuelSavingsParams, VehicleTab } from '../types';
import {
    buildExpandedTrades,
    buildTradeValuesByVehicle,
    calculateFuelSavings,
    CROSS_TAB_FIELDS,
    CROSS_TRADE_FIELDS,
    DEFAULT_TRADE_FORM_VALUES,
    prefillTradeInMpgValues,
    prefillNewVehicleMpgValues,
    backfillNewVehiclePricePerGallonValues
} from '../utils';

// Module-level stable defaults prevent new array references on every render,
// which would cause the useEffect to fire infinitely when these are used as deps.
const EMPTY_VEHICLE_TABS: VehicleTab[] = [];
const EMPTY_TRADES: TradeSectionData[] = [];

export const useFuelSavings = ({
    isFuelSavingCardState,
    vehicleTabs = EMPTY_VEHICLE_TABS,
    trades = EMPTY_TRADES,
    fuelEconomyData,
    baggageFuelEconomy
}: UseFuelSavingsParams) => {
    const safeVehicleTabs = Array.isArray(vehicleTabs) ? vehicleTabs : [];
    const safeTrades = Array.isArray(trades) ? trades : [];

    const [activeVehicleId, setActiveVehicleId] = useState<string>(() => safeVehicleTabs[0]?.id || '');
    const [expandedTrades, setExpandedTrades] = useState<Record<string, boolean>>(() => buildExpandedTrades(safeTrades));
    // Store trade values per vehicle tab: tradeValues[vehicleId][tradeId]
    const [tradeValuesByVehicle, setTradeValuesByVehicle] = useState<Record<string, Record<string, FuelSavingFormValues>>>(() =>
        buildTradeValuesByVehicle(safeVehicleTabs, safeTrades, baggageFuelEconomy)
    );

    // Track previous slideout state to detect when it opens
    const prevIsFuelSavingCardState = useRef(isFuelSavingCardState);

    // Always-current snapshot of the values read inside effects so effects
    // can use fresh data without those values being reactive deps.
    const latestRef = useRef({ safeVehicleTabs, safeTrades, baggageFuelEconomy });
    latestRef.current = { safeVehicleTabs, safeTrades, baggageFuelEconomy };

    // Initialize state when slideout opens (transition from closed to open).
    // Reads latest values via ref — no stale closure risk, no extra re-runs.
    useEffect(() => {
        const wasOpened = !prevIsFuelSavingCardState.current && isFuelSavingCardState;
        prevIsFuelSavingCardState.current = isFuelSavingCardState;

        if (!wasOpened) {
            return;
        }

        const { safeVehicleTabs: tabs, safeTrades: tradesData, baggageFuelEconomy: baggage } = latestRef.current;
        const firstTabId = tabs[0]?.id || '';

        setActiveVehicleId(firstTabId);
        setExpandedTrades(buildExpandedTrades(tradesData));
        setTradeValuesByVehicle(buildTradeValuesByVehicle(tabs, tradesData, baggage));
    }, [isFuelSavingCardState]);

    // When vehicleTabs gains new tabs (e.g. after SCENARIOS_FETCH_SUCCESS), initialize their
    // trade values without resetting already-initialized tabs.
    // isFuelSavingCardState included so the guard is always fresh.
    useEffect(() => {
        if (!isFuelSavingCardState) return;
        const { safeTrades: tradesData, baggageFuelEconomy: baggage } = latestRef.current;
        setTradeValuesByVehicle((previous) => {
            const newTabs = safeVehicleTabs.filter((tab) => !(tab.id in previous));
            // Initialize entries for any newly arrived tabs (e.g. scenario vehicles after SCENARIOS_FETCH_SUCCESS).
            const withNewTabs =
                newTabs.length === 0 ? previous : { ...previous, ...buildTradeValuesByVehicle(newTabs, tradesData, baggage) };
            // Backfill newVehiclePricePerGallon from baggage for all tabs. This handles the case where
            // a scenario tab was initialized before baggage was available. backfillNewVehiclePricePerGallonValues
            // returns the same reference when nothing changed, so it is safe to call on every vehicleTabs change.
            return backfillNewVehiclePricePerGallonValues(withNewTabs, baggage);
        });
    }, [vehicleTabs, isFuelSavingCardState]);

    const activeVehicle = useMemo(
        () => safeVehicleTabs.find((vehicle) => vehicle.id === activeVehicleId) ?? safeVehicleTabs[0],
        [activeVehicleId, safeVehicleTabs]
    );
    const activeVehicleTabId = activeVehicle ? activeVehicle.id : '';
    // Use trades prop directly instead of getting from active vehicle
    const activeTrades = safeTrades;

    // Get trade values for the active vehicle tab
    const tradeValues = tradeValuesByVehicle[activeVehicleTabId] || {};

    useEffect(() => {
        const tradeinVehiclesVin = fuelEconomyData?.data?.tradeinVehiclesVin;
        const sellingVehiclesVin = fuelEconomyData?.data?.sellingVehiclesVin;

        if (tradeinVehiclesVin) {
            setTradeValuesByVehicle((previous) => {
                return prefillTradeInMpgValues(previous, safeTrades, tradeinVehiclesVin);
            });
        }

        if (sellingVehiclesVin) {
            setTradeValuesByVehicle((previous) => {
                return prefillNewVehicleMpgValues(previous, safeVehicleTabs, sellingVehiclesVin);
            });
        }
    }, [fuelEconomyData, safeTrades, safeVehicleTabs]);

    const toggleTradeSection = (tradeId: string) => {
        setExpandedTrades((previous) => ({
            ...previous,
            [tradeId]: !previous[tradeId]
        }));
    };

    const updateTradeInput = (tradeId: string, field: keyof FuelSavingFormValues, value: string | Period) => {
        const propagateToAllTrades = CROSS_TRADE_FIELDS.has(field);
        const propagateToAllTabs = CROSS_TAB_FIELDS.has(field);

        setTradeValuesByVehicle((previousByVehicle) => {
            // Determine which vehicle tabs to update.
            // Cross-tab fields update all existing tabs; all other fields only update the active tab.
            // The active tab is always included even if it has no entry yet.
            const tabIdsToUpdate = new Set(propagateToAllTabs ? Object.keys(previousByVehicle) : []);
            tabIdsToUpdate.add(activeVehicleTabId);

            const updatedByVehicle: Record<string, Record<string, FuelSavingFormValues>> = {
                ...previousByVehicle
            };

            for (const vehicleId of tabIdsToUpdate) {
                const vehicleTradeValues = previousByVehicle[vehicleId] ?? {};

                if (propagateToAllTrades) {
                    // Apply the field change to every trade in this vehicle tab so that
                    // vehicle-common values (new vehicle MPG/price, driving habits) stay in sync.
                    const updatedTrades: Record<string, FuelSavingFormValues> = Object.fromEntries(
                        Object.entries(vehicleTradeValues).map(([tid, tValues]) => [tid, { ...tValues, [field]: value }])
                    );
                    // Also ensure the target tradeId exists when it is a new/unknown trade.
                    if (!(tradeId in vehicleTradeValues)) {
                        updatedTrades[tradeId] = { ...DEFAULT_TRADE_FORM_VALUES, [field]: value };
                    }
                    updatedByVehicle[vehicleId] = updatedTrades;
                } else {
                    // Apply only to the specific trade (e.g. tradeInMpg is unique per trade-in vehicle).
                    const currentTradeValues = vehicleTradeValues[tradeId] ?? DEFAULT_TRADE_FORM_VALUES;
                    updatedByVehicle[vehicleId] = {
                        ...vehicleTradeValues,
                        [tradeId]: { ...currentTradeValues, [field]: value }
                    };
                }
            }

            return updatedByVehicle;
        });
    };

    const tradeViewModels = useMemo(
        () =>
            activeTrades
                .filter((trade) => trade.vin) // Filter out trades without VIN
                .map((trade, index) => {
                    const values = {
                        ...DEFAULT_TRADE_FORM_VALUES,
                        ...(tradeValues[trade.id] ?? {})
                    };

                    // Check all required fields have non-empty values before calculating
                    const allFieldsFilled =
                        values.newVehicleMpg !== '' &&
                        values.tradeInMpg !== '' &&
                        values.newVehiclePricePerGallon !== '' &&
                        values.tradePricePerGallon !== '' &&
                        values.milesDriven !== '';

                    // Only calculate when all fields are filled - skip computation for blank forms
                    if (!allFieldsFilled) {
                        return {
                            trade,
                            index,
                            values,
                            isExpanded: Boolean(expandedTrades[trade.id]),
                            showResults: false,
                            savings: null
                        };
                    }

                    const newVehicleMpg = Number(values.newVehicleMpg);
                    const tradeInMpg = Number(values.tradeInMpg);
                    const newVehiclePrice = Number(values.newVehiclePricePerGallon);
                    const tradePrice = Number(values.tradePricePerGallon);
                    const milesDriven = Number(values.milesDriven);

                    const { yearlySavings, monthlySavings, threeYearSavings } = calculateFuelSavings(
                        milesDriven,
                        values.milesPer,
                        newVehicleMpg,
                        tradeInMpg,
                        newVehiclePrice,
                        tradePrice
                    );

                    const hasSavings =
                        typeof monthlySavings === 'number' &&
                        typeof yearlySavings === 'number' &&
                        typeof threeYearSavings === 'number';

                    return {
                        trade,
                        index,
                        values,
                        isExpanded: Boolean(expandedTrades[trade.id]),
                        showResults: hasSavings,
                        savings: {
                            monthly: monthlySavings,
                            yearly: yearlySavings,
                            threeYears: threeYearSavings
                        }
                    };
                }),
        [activeTrades, tradeValues, expandedTrades]
    );

    // Check if at least one trade has valid results (form filled and savings calculated)
    const hasValidResults = useMemo(() => tradeViewModels.some((vm) => vm.showResults), [tradeViewModels]);

    const buildApplyPayload = useCallback(() => {
        const allVehiclesData = safeVehicleTabs.map((tab) => {
            const tabTradeValues = tradeValuesByVehicle[tab.id] || {};

            const trades = safeTrades
                .map((trade) => {
                    const values = tabTradeValues[trade.id];

                    // Skip trades that haven't been filled for this vehicle
                    if (!values) {
                        return null;
                    }

                    const allFieldsFilled =
                        values.newVehicleMpg !== '' &&
                        values.tradeInMpg !== '' &&
                        values.newVehiclePricePerGallon !== '' &&
                        values.tradePricePerGallon !== '' &&
                        values.milesDriven !== '';

                    if (!allFieldsFilled) {
                        return {
                            id: trade.id,
                            name: trade.name,
                            vin: trade.vin,
                            values,
                            savings: null
                        };
                    }

                    const newVehicleMpg = Number(values.newVehicleMpg);
                    const tradeInMpg = Number(values.tradeInMpg);
                    const newVehiclePrice = Number(values.newVehiclePricePerGallon);
                    const tradePrice = Number(values.tradePricePerGallon);
                    const milesDriven = Number(values.milesDriven);

                    const { yearlySavings, monthlySavings, threeYearSavings } = calculateFuelSavings(
                        milesDriven,
                        values.milesPer,
                        newVehicleMpg,
                        tradeInMpg,
                        newVehiclePrice,
                        tradePrice
                    );

                    return {
                        id: trade.id,
                        name: trade.name,
                        vin: trade.vin,
                        values,
                        savings: {
                            monthly: monthlySavings,
                            yearly: yearlySavings,
                            threeYears: threeYearSavings
                        }
                    };
                })
                .filter((item): item is NonNullable<typeof item> => item !== null);

            return {
                vehicle: {
                    id: tab.id,
                    label: tab.label,
                    isPrimary: tab.isPrimary
                },
                trades
            };
        });

        return {
            vehicle: activeVehicle
                ? { id: activeVehicle.id, label: activeVehicle.label, isPrimary: activeVehicle.isPrimary }
                : undefined,
            trades: tradeViewModels.map(({ trade, savings }) => ({
                id: trade.id,
                name: trade.name,
                vin: trade.vin,
                values: tradeValues[trade.id],
                savings
            })),
            allVehicles: allVehiclesData
        };
    }, [activeVehicle, safeVehicleTabs, safeTrades, tradeViewModels, tradeValues, tradeValuesByVehicle]);

    return {
        safeVehicleTabs,
        activeVehicleTabId,
        tradeViewModels,
        tradeValues,
        buildApplyPayload,
        hasValidResults,
        setActiveVehicleId,
        toggleTradeSection,
        updateTradeInput
    };
};
